import { AssistantChat } from "@components/assistant-ui/AssistantChat.component";
import AiService from "@services/ai.service";
import ApiService from "@services/api/api.service";
import { getErrorMessage } from "@services/errors.service";
import { useCallback, useEffect, useState } from "react";
import { useImmer } from "use-immer";
import { Modal, Button, Form, Card, Collapse } from "react-bootstrap";
import styled from "styled-components";

const Wrapper = styled.div`

.list-group-item {
    padding: 2rem;
}
`
interface DataModelInferenceProps {
    // Full table names including database and schema
    tables: string[];
    onLoadComplete?: (entities: DataModelInferenceResponse, relationships: RelationshipsResponse, metrics: MetricsResponse) => void;
    onLoadingChange?: (isLoading: boolean) => void;
}

interface TableColumn {
    column_name: string;
    data_type: string;
}

interface TableColumnsResponse {
    [table_name: string]: TableColumn[];
}

export interface DataModelInferenceResponse {
    explanation: string;
    tables: {
        source_table_name: string;
        data_model_table_name: string;
        columns: {
            source_column_name: string;
            recommended_column_name: string;
            data_type: string;
            use_as_dimension: boolean;
        }[];
    }[];
}

interface Relationship {
    parent_table_name: string;
    child_table_name: string;
    parent_column_name: string;
    child_column_name: string;
    relationship_name: string;
}

export interface RelationshipsResponse {
    relationships: Relationship[];
}

interface Metric {
    name: string;
    type: 'SIMPLE' | 'CALCULATED';
    formula?: string;
    parent_metric_names?: string[];
    source_table_name?: string;
    source_column_name?: string;
    aggregation_type?: 'SUM' | 'COUNT' | 'AVERAGE' | 'MIN' | 'MAX' | 'COUNT_DISTINCT';
}

export interface MetricsResponse {
    metrics: Metric[];
}

interface StepState {
    loading: boolean;
    error: string;
    completed: boolean;
}

interface DataModelTable {
    table_name: string;
    columns: {
        column_name: string;
        data_type: string;
    }[];
}




interface DataModelEntityGridProps {
    data: DataModelInferenceResponse;
    onChange?: (updated: DataModelInferenceResponse) => void;
}

const DATA_TYPES = [
    "TEXT",
    "NUMBER",
    "FLOAT",
    "BOOLEAN",
    "DATE",
    "TIMESTAMP"
];

const DataModelEntityGrid: React.FC<DataModelEntityGridProps> = ({ data, onChange }) => {
    const [selectedEntityIdx, setSelectedEntityIdx] = useState<number | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [editEntity, setEditEntity] = useState<typeof data.tables[0] | null>(null);

    // Handle opening modal and setting up edit state
    const handleEntityClick = (idx: number) => {
        setSelectedEntityIdx(idx);
        setEditEntity(JSON.parse(JSON.stringify(data.tables[idx]))); // deep copy
        setShowModal(true);
    };

    // Handle closing modal
    const handleClose = () => {
        setShowModal(false);
        setEditEntity(null);
        setSelectedEntityIdx(null);
    };

    // Handle column name change
    const handleColumnNameChange = (colIdx: number, value: string) => {
        if (!editEntity) return;
        const updated = { ...editEntity };
        updated.columns = updated.columns.map((col, i) =>
            i === colIdx ? { ...col, recommended_column_name: value } : col
        );
        setEditEntity(updated);
    };

    // Handle data type change
    const handleDataTypeChange = (colIdx: number, value: string) => {
        if (!editEntity) return;
        const updated = { ...editEntity };
        updated.columns = updated.columns.map((col, i) =>
            i === colIdx ? { ...col, data_type: value } : col
        );
        setEditEntity(updated);
    };

    // Handle removing a column
    const handleRemoveColumn = (colIdx: number) => {
        if (!editEntity) return;
        const updated = { ...editEntity };
        updated.columns = updated.columns.filter((_, i) => i !== colIdx);
        setEditEntity(updated);
    };

    // Handle adding a new column
    const handleAddColumn = () => {
        if (!editEntity) return;
        const updated = { ...editEntity };
        updated.columns = [
            ...updated.columns,
            {
                source_column_name: "",
                recommended_column_name: "",
                data_type: "TEXT",
                use_as_dimension: false
            }
        ];
        setEditEntity(updated);
    };

    // Handle saving changes
    const handleSave = () => {
        if (selectedEntityIdx === null || !editEntity) return;
        const updatedData = {
            ...data,
            tables: data.tables.map((t, i) => (i === selectedEntityIdx ? editEntity : t))
        };
        if (onChange) onChange(updatedData);
        setShowModal(false);
        setEditEntity(null);
        setSelectedEntityIdx(null);
    };

    return (
        <div>
            <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 400px))",
                gap: "1rem",
                marginTop: "1.5rem"
            }}>
                {data.tables.map((entity, idx) => (
                    <div
                        key={entity.data_model_table_name}
                        className="card"
                        style={{ cursor: "pointer", minHeight: 120, borderTop: 'solid 5px var(--brand-purple)' }}
                        onClick={() => handleEntityClick(idx)}
                    >
                        <div className="card-body">
                            <h2 className="mb-0">{entity.data_model_table_name}</h2>
                            <div style={{ fontSize: 12, color: "#888" }}>
                                {entity.columns.length} columns
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <Modal show={showModal} onHide={handleClose} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editEntity?.data_model_table_name || "Entity"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <div style={{ maxHeight: 350, overflowY: "auto" }}>
                            <table className="table table-sm table-centered table-bordered">
                                <thead className="bg-light">
                                    <tr>
                                        <th>Source Column</th>
                                        <th>Recommended Name</th>
                                        <th>Data Type</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {editEntity?.columns.map((col, colIdx) => (
                                        <tr key={colIdx}>
                                            <td>
                                                <Form.Control
                                                    type="text"
                                                    value={col.source_column_name}
                                                    onChange={e => {
                                                        const updated = { ...editEntity };
                                                        updated.columns = updated.columns.map((c, i) =>
                                                            i === colIdx
                                                                ? { ...c, source_column_name: e.target.value }
                                                                : c
                                                        );
                                                        setEditEntity(updated);
                                                    }}
                                                    placeholder="Source column name"
                                                />
                                            </td>
                                            <td>
                                                <Form.Control
                                                    type="text"
                                                    value={col.recommended_column_name}
                                                    onChange={e => handleColumnNameChange(colIdx, e.target.value)}
                                                    placeholder="Recommended name"
                                                />
                                            </td>
                                            <td>
                                                <Form.Select
                                                    value={col.data_type}
                                                    onChange={e => handleDataTypeChange(colIdx, e.target.value)}
                                                >
                                                    {DATA_TYPES.map(dt => (
                                                        <option key={dt} value={dt}>{dt}</option>
                                                    ))}
                                                </Form.Select>
                                            </td>
                                            <td>
                                                <button
                                                    className="icon-button"
                                                    onClick={() => handleRemoveColumn(colIdx)}
                                                    title="Remove column"
                                                >
                                                    <i className="mdi mdi-delete"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleAddColumn}>
                        + Add Column
                    </Button>
                    <Button variant="success" onClick={handleSave}>
                        Save Changes
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export { DataModelEntityGrid };



interface RelationshipsGridProps {
    data: RelationshipsResponse;
}

const RelationshipsGrid: React.FC<RelationshipsGridProps> = ({ data }) => {
    if (!data || !data.relationships || data.relationships.length === 0) {
        return <div>No relationships found.</div>;
    }

    return (
        <div className="d-flex flex-wrap gap-3">
            {data.relationships.map((rel, idx) => (
                <Card key={idx} style={{ minWidth: 260, maxWidth: 320 }}>
                    <Card.Body>
                        <Card.Title className="mb-2" style={{ fontSize: "1.1em" }}>
                            {rel.relationship_name}
                        </Card.Title>
                        <div>
                            <strong>Parent Table:</strong> {rel.parent_table_name}
                        </div>
                        <div>
                            <strong>Parent Column:</strong> {rel.parent_column_name}
                        </div>
                        <div>
                            <strong>Child Table:</strong> {rel.child_table_name}
                        </div>
                        <div>
                            <strong>Child Column:</strong> {rel.child_column_name}
                        </div>
                    </Card.Body>
                </Card>
            ))}
        </div>
    );
};

export { RelationshipsGrid };

interface MetricsGridProps {
    data: MetricsResponse;
}

const MetricsGrid: React.FC<MetricsGridProps> = ({ data }) => {
    if (!data || !data.metrics || data.metrics.length === 0) {
        return <div>No metrics found.</div>;
    }

    return (
        <div className="d-flex flex-wrap gap-3">
            {data.metrics.map((metric, idx) => (
                <Card key={idx} style={{ minWidth: 260, maxWidth: 340,  borderTop: 'solid 5px var(--brand-green)' }}>
                    <Card.Body>
                        <h2 className="mb-1">{metric.name}</h2>
                        <div>
                            <strong>Type:</strong> {metric.type}
                        </div>
                        {metric.type === "SIMPLE" && (
                            <>
                                <div>
                                    <strong>Table:</strong> {metric.source_table_name}
                                </div>
                                <div>
                                    <strong>Column:</strong> {metric.source_column_name}
                                </div>
                                <div>
                                    <strong>Aggregation:</strong> {metric.aggregation_type}
                                </div>
                            </>
                        )}
                        {metric.type === "CALCULATED" && (
                            <>
                                <div>
                                    <strong>Formula:</strong> {metric.formula || <span className="text-muted">N/A</span>}
                                </div>
                                <div>
                                    <strong>Parent Metrics:</strong>{" "}
                                    {metric.parent_metric_names && metric.parent_metric_names.length > 0
                                        ? metric.parent_metric_names.join(", ")
                                        : <span className="text-muted">N/A</span>}
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>
            ))}
        </div>
    );
};

export { MetricsGrid };



const DataModelInference = ({ tables, onLoadComplete, onLoadingChange }: DataModelInferenceProps) => {
    
    const [activeSection, setActiveSection] = useState<string | null>(null);
    
    const [stepStates, setStepStates] = useImmer<{
        loadTableColumns: StepState;
        inferEntities: StepState;
        inferRelationships: StepState;
        inferMetrics: StepState;
    }>({
        loadTableColumns: {
            loading: false,
            error: '',
            completed: false
        },
        inferEntities: {
            loading: false,
            error: '',
            completed: false
        },
        inferRelationships: {
            loading: false,
            error: '',
            completed: false
        },
        inferMetrics: {
            loading: false,
            error: '',
            completed: false
        }
    });
    const [tableColumns, setTableColumns] = useState<TableColumnsResponse>({});
    const [dataModelInference, setDataModelInference] = useState<DataModelInferenceResponse>({
        explanation: '',
        tables: []
    }); 
    const [dataModelTables, setDataModelTables] = useState<DataModelTable[]>([]);
    const [dataModelRelationships, setDataModelRelationships] = useState<RelationshipsResponse>({
        relationships: []
    });
    const [dataModelMetrics, setDataModelMetrics] = useState<MetricsResponse>({
        metrics: []
    });

    // Function to parse dataModelTables from dataModelInference
    const parseDataModelTables = useCallback((dataModelInference: DataModelInferenceResponse): DataModelTable[] => {
        const tables = (dataModelInference?.tables || []).map(table => ({
            table_name: table.data_model_table_name,
            columns: (table.columns || []).map(col => ({
                column_name: col.recommended_column_name,
                data_type: col.data_type
            }))
        }));

        // Add hard-coded date dimension table
        const dateDimensionTable: DataModelTable = {
            table_name: "Calendar",
            columns: [
                { column_name: "DATE", data_type: "DATE" },
                { column_name: "FIRST_DAY_OF_MONTH", data_type: "DATE" },
                { column_name: "FIRST_DAY_OF_WEEK", data_type: "DATE" },
                { column_name: "FIRST_DAY_OF_QUARTER", data_type: "DATE" },
                { column_name: "FIRST_DAY_OF_YEAR", data_type: "DATE" }
            ]
        };

        return [...tables, dateDimensionTable];
    }, []);

    const handleToolCall = useCallback((toolCall: any) => {
        console.log('Tool call received:', toolCall);
    }, []);

    const loadTableColumns = useCallback(async () => {
        setStepStates(draft => {
            draft.loadTableColumns.loading = true;
        });
        let rv: TableColumnsResponse = {};
        try {
            const response = await ApiService.getInstance().request('POST', '/sql/batch-describe-tables', { tables }) as TableColumnsResponse;
            setTableColumns(response);
            rv = response;
        setStepStates(draft => {
            draft.loadTableColumns.completed = true;
        });
        setActiveSection('loadTableColumns');
        } catch (error) {
            setStepStates(draft => {
                draft.loadTableColumns.error = getErrorMessage(error);
            });
        } finally {
            setStepStates(draft => {
                draft.loadTableColumns.loading = false;
            });
        }
        return rv;
    }, [tables]);

    const [loadingInferEntities, setLoadingInferEntities] = useState(false);

    const inferEntities = useCallback(async (tableColumns: TableColumnsResponse) => {
        const prompt = `
        I have the following tables and columns in my database:
        ${JSON.stringify(tableColumns)}

        I want you to help me create a semantic layer for the tables I selected - specifically, I want you to give each table and column a more human-readable name and describe the type of business you think it is. Keep things as granular and hierarchical as you can - we want a flexible data model that can be used for a variety of purposes. Your response should be in JSON format with the following structure:

        {
            "explanation": "A short explanation of your reasoning",
            "tables": [
                {
                    "source_table_name": "source_table_name",
                    "data_model_table_name": "human_readable table name",
                    "columns": [{
                        "source_column_name": "source_column_name",
                        "recommended_column_name": "human_readable column name",
                        "data_type": "data type of the column (Snowflake SQL)",
                        "use_as_dimension": "boolean indicating whether the column should be used as a dimension in reporting"
                    }]
                }
            ]
        }
        `;
        setStepStates(draft => {
            draft.inferEntities.loading = true;
            draft.inferEntities.error = '';
            draft.inferEntities.completed = false;
        });

        let rv: DataModelInferenceResponse = {
            explanation: '',
            tables: []
        };
        try {
            const result = await AiService.getInstance().syncCompletion(prompt) as DataModelInferenceResponse;
            console.log('Infer entities result:', result);
            setDataModelInference(result);
            setStepStates(draft => {
                draft.inferEntities.completed = true;
            });
            setActiveSection('inferEntities');
            rv = result;
        } catch (err) {
            setStepStates(draft => {
                draft.inferEntities.error = getErrorMessage(err);
            });
        } finally {
            setStepStates(draft => {
                draft.inferEntities.loading = false;
            });
        }
        return rv;
    }, [tableColumns]);

    const inferRelationships = useCallback(async (dataModelTables: DataModelTable[]) => {
        const prompt = `
        I have the following tables and columns in my database:
        ${JSON.stringify(dataModelTables)}

        I want you to help me create parent-child relationships between the tables in my database. Give me a list of parent-child relationships, and the columns that are used to join them. Your response should be in JSON format with the following structure, with no additional content besides the JSON:

        {
            "relationships": [
                {
                    "parent_table_name": "parent_table_name",
                    "child_table_name": "child_table_name",
                    "parent_column_name": "parent_column_name",
                    "child_column_name": "child_column_name",
                    "relationship_name": "relationship_name"
                }
            ]
        }
        `;

        let rv: RelationshipsResponse = {
            relationships: []
        };

        setStepStates(draft => {
            draft.inferRelationships.loading = true;
            draft.inferRelationships.error = '';
            draft.inferRelationships.completed = false;
        });
        try {
            const result = await AiService.getInstance().syncCompletion(prompt) as RelationshipsResponse;
            console.log('Infer relationships result:', result);
            setDataModelRelationships(result);
            setStepStates(draft => {
                draft.inferRelationships.completed = true;
            });
            setActiveSection('inferRelationships');
            rv = result;
        } catch (err) {
            setStepStates(draft => {
                draft.inferRelationships.error = getErrorMessage(err);
            });
        } finally {
            setStepStates(draft => {
                draft.inferRelationships.loading = false;
            });
        }
        return rv;
    }, []);

    const inferMetrics = useCallback(async (dataModelTables: DataModelTable[], relationships: RelationshipsResponse) => {
        const prompt = `
        I have the following tables, columns, and relationships in my database:
        ${JSON.stringify(dataModelTables)}

        RELATIONSHIPS:
        ${JSON.stringify(relationships)}

        I want you to help me create metrics for the tables in my database. Give me a list of metrics, and the columns that are used to calculate them. Your response should be in JSON format with the following structure, with no additional content besides the JSON. Note that formula and parent_metric_names are only necessary for CALCULATED metrics, and source_table_name, source_column_name, and aggregation_type are only necessary for SIMPLE metrics. If the metric is a SIMPLE metric, make sure the aggregation type is one of SUM, COUNT, AVERAGE, MIN, MAX, COUNT_DISTINCT, and nothing else.

        {
            "metrics": [
                {
                    "name": "metric_name",
                    "type": "SIMPLE or CALCULATED. SIMPLE means the metric is a simple aggregation of a column, and CALCULATED means the metric is a calculated value based on other metrics.",
                    "formula": "formula for the metric. Not necessary for SIMPLE metrics.",
                    "parent_metric_names": ["metric_name1", "metric_name2"],
                    "source_table_name": "table_name",
                    "source_column_name": "column_name",
                    "aggregation_type": "SUM, COUNT, AVERAGE, MIN, MAX, COUNT_DISTINCT"
                }
            ]
        }
        `;

        setStepStates(draft => {
            draft.inferMetrics.loading = true;
            draft.inferMetrics.error = '';
            draft.inferMetrics.completed = false;
        });

        let rv: MetricsResponse = {
            metrics: []
        };

        try {
            const result = await AiService.getInstance().syncCompletion(prompt) as MetricsResponse;
            setDataModelMetrics(result);
            setStepStates(draft => {
                draft.inferMetrics.completed = true;
            });
            setActiveSection('inferMetrics');
            rv = result;
        } catch (err) {
            setStepStates(draft => {
                draft.inferMetrics.error = getErrorMessage(err);
            });
        } finally {
            setStepStates(draft => {
                draft.inferMetrics.loading = false;
            });
        }
        return rv;
    }, []);
    const [loadingAll, setLoadingAll] = useState(false);
    const [isComplete, setIsComplete] = useState(false);

    // Notify parent component when loading state changes
    useEffect(() => {
        if (onLoadingChange) {
            onLoadingChange(loadingAll);
        }
    }, [loadingAll, onLoadingChange]);

    const loadAll = useCallback(async () => {
        if (loadingAll) {
            // Don't double-load
            return;
        }
        setLoadingAll(true);
        const tableColumnsData = await loadTableColumns();
        const dataModelInferenceResult = await inferEntities(tableColumnsData);
        
        // Parse dataModelTables from the inference result
        const parsedDataModelTables = parseDataModelTables(dataModelInferenceResult);
        setDataModelTables(parsedDataModelTables);
        
        // Pass the parsed dataModelTables to both inferRelationships and inferMetrics
        const relationships = await inferRelationships(parsedDataModelTables);
        const metrics = await inferMetrics(parsedDataModelTables, relationships);

        
        // Call onLoadComplete with all the inferred data
        if (onLoadComplete) {
            onLoadComplete(dataModelInferenceResult, relationships, metrics);
        }
        
        setIsComplete(true);
        setLoadingAll(false);
    }, [loadingAll, loadTableColumns, inferEntities, inferRelationships, inferMetrics, parseDataModelTables, onLoadComplete]);


    // Removed automatic loading on mount - now triggered by button click



    const handleSectionClick = (sectionId: string) => {
        setActiveSection(activeSection === sectionId ? null : sectionId);
    };

    return <Wrapper>
        <div className="d-flex justify-content-center mb-4">
            <button
                className="btn btn-pliable btn-lg"
                onClick={loadAll}
                disabled={loadingAll}
                style={{ minWidth: '200px' }}
            >
                {loadingAll ? (
                    <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-label="Loading" />
                        Running AI...
                    </>
                ) : (
                    <>
                        <i className="mdi mdi-robot me-2"></i>
                        Run AI
                    </>
                )}
            </button>
        </div>
        <div className="list-group">
            <div className="list-group-item">
                <div 
                    className="d-flex align-items-center mb-2" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSectionClick('loadTableColumns')}
                >
                    <h3 className="mb-0 me-2">Load table structure</h3>
                    {stepStates.loadTableColumns.loading && (
                        <span className="spinner-border spinner-border-sm text-primary" role="status" aria-label="Loading" />
                    )}
                    {stepStates.loadTableColumns.error && !stepStates.loadTableColumns.loading &&(
                        <span className="ms-1 text-danger" title="Error">
                            <i className="mdi mdi-alert-circle" style={{ fontSize: '1.2em' }}></i>
                        </span>
                    )}
                    {stepStates.loadTableColumns.completed && !stepStates.loadTableColumns.error && !stepStates.loadTableColumns.loading &&(
                        <span className="ms-1 text-success" title="Completed">
                            <i className="mdi mdi-check-circle" style={{ fontSize: '1.2em' }}></i>
                        </span>
                    )}
                    <i className={`mdi mdi-chevron-${activeSection === 'loadTableColumns' ? 'up' : 'down'} ms-auto`} style={{ fontSize: '1.5em' }}></i>
                </div>
                <Collapse in={activeSection === 'loadTableColumns'}>
                    <div>
                        <p>Loading table structure and column information from your database.</p>
                    </div>
                </Collapse>
            </div>
            
            <div className="list-group-item">
                <div 
                    className="d-flex align-items-center mb-2" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSectionClick('inferEntities')}
                >
                    <h3 className="mb-0 me-2">Infer entities</h3>
                    {stepStates.inferEntities.loading && (
                        <span className="spinner-border spinner-border-sm text-primary" role="status" aria-label="Loading" />
                    )}
                    {stepStates.inferEntities.error && !stepStates.inferEntities.loading &&(
                        <span className="ms-1 text-danger" title="Error">
                            <i className="mdi mdi-alert-circle" style={{ fontSize: '1.2em' }}></i>
                        </span>
                    )}
                    {stepStates.inferEntities.completed && !stepStates.inferEntities.error && !stepStates.inferEntities.loading &&(
                        <span className="ms-1 text-success" title="Completed">
                            <i className="mdi mdi-check-circle" style={{ fontSize: '1.2em' }}></i>
                        </span>
                    )}
                    <i className={`mdi mdi-chevron-${activeSection === 'inferEntities' ? 'up' : 'down'} ms-auto`} style={{ fontSize: '1.5em' }}></i>
                </div>
                <Collapse in={activeSection === 'inferEntities'}>
                    <div>
                        <p>Entities are the main objects in your data model, typically representing real-world objects or concepts like an Order or a Customer.</p>
                        {stepStates.inferEntities.completed && <code>{dataModelInference.explanation}</code>}
                        {stepStates.inferEntities.completed && <>
                            <DataModelEntityGrid data={dataModelInference} />
                        </>}
                    </div>
                </Collapse>
            </div>
            
            <div className="list-group-item">
                <div 
                    className="d-flex align-items-center mb-2" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSectionClick('inferRelationships')}
                >
                    <h3 className="mb-0 me-2">Infer relationships</h3>
                    {stepStates.inferRelationships.loading && (
                        <span className="spinner-border spinner-border-sm text-primary" role="status" aria-label="Loading" />
                    )}
                    {stepStates.inferRelationships.error && !stepStates.inferRelationships.loading &&(
                        <span className="ms-1 text-danger" title="Error">
                            <i className="mdi mdi-alert-circle" style={{ fontSize: '1.2em' }}></i>
                        </span>
                    )}
                    {stepStates.inferRelationships.completed && !stepStates.inferRelationships.error && !stepStates.inferRelationships.loading &&(
                        <span className="ms-1 text-success" title="Completed">
                            <i className="mdi mdi-check-circle" style={{ fontSize: '1.2em' }}></i>
                        </span>
                    )}
                    <i className={`mdi mdi-chevron-${activeSection === 'inferRelationships' ? 'up' : 'down'} ms-auto`} style={{ fontSize: '1.5em' }}></i>
                </div>
                <Collapse in={activeSection === 'inferRelationships'}>
                    <div>
                        <p>Relationships are the connections between entities, representing how one entity is related to another. For example, an Order may have a relationship with a Customer (the customer made the order), or a Product may have a relationship with an Order (the order contains the product).</p>
                        {stepStates.inferRelationships.completed && <RelationshipsGrid data={dataModelRelationships} />}
                    </div>
                </Collapse>
            </div>
            
            <div className="list-group-item">
                <div 
                    className="d-flex align-items-center mb-2" 
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSectionClick('inferMetrics')}
                >
                    <h3 className="mb-0 me-2">Infer metrics</h3>
                    {stepStates.inferMetrics.loading && (
                        <span className="spinner-border spinner-border-sm text-primary" role="status" aria-label="Loading" />
                    )}
                    {stepStates.inferMetrics.error && !stepStates.inferMetrics.loading &&(
                        <span className="ms-1 text-danger" title="Error">
                            <i className="mdi mdi-alert-circle" style={{ fontSize: '1.2em' }}></i>
                        </span>
                    )}
                    {stepStates.inferMetrics.completed && !stepStates.inferMetrics.error && !stepStates.inferMetrics.loading &&(
                        <span className="ms-1 text-success" title="Completed">
                            <i className="mdi mdi-check-circle" style={{ fontSize: '1.2em' }}></i>
                        </span>
                    )}
                    <i className={`mdi mdi-chevron-${activeSection === 'inferMetrics' ? 'up' : 'down'} ms-auto`} style={{ fontSize: '1.5em' }}></i>
                </div>
                <Collapse in={activeSection === 'inferMetrics'}>
                    <div>
                        <p>Metrics are the calculations you want to perform on your data. For example, you might want to calculate the total revenue for a given period.</p>
                        {stepStates.inferMetrics.completed && <MetricsGrid data={dataModelMetrics} />}
                    </div>
                </Collapse>
            </div>
        </div>
    </Wrapper>;
};

export default DataModelInference;